import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getPublicIntakeForm, submitPublicIntakeForm, uploadPublicIntakeFile, API_BASE } from "../lib/api";
import { getFormById } from "../lib/intakeForms";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Loader2, Send, FileText, Upload, X as XIcon, CheckCircle2 } from "lucide-react";

// Brand-neutral public intake page. NO sidebar, NO portal branding.
// Resellers can confidently share this link with end clients.
export default function PublicIntakePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    getPublicIntakeForm(token)
      .then((res) => setMeta(res.data))
      .catch((err) => setError(err.response?.data?.detail || "This link could not be opened."));
  }, [token]);

  const schema = meta ? getFormById(meta.form_type) : null;

  // Dynamic browser tab title — no "Emergent | Fullstack App" leak. Resets on unmount.
  useEffect(() => {
    const original = document.title;
    if (meta) {
      const formLabel = schema?.name || meta.form_name || "Intake Form";
      const senderLabel = meta.sender_label || "";
      document.title = senderLabel ? `${formLabel} — ${senderLabel}` : formLabel;
    }
    return () => { document.title = original; };
  }, [meta, schema]);

  // Resolve absolute URL for the agency logo (the API returns a relative /api/... path)
  const agencyLogoSrc = meta?.agency_logo_url
    ? (meta.agency_logo_url.startsWith("http") ? meta.agency_logo_url : `${API_BASE}${meta.agency_logo_url}`)
    : null;

  const updateField = (id, value) => setFormData((p) => ({ ...p, [id]: value }));
  const toggleCheckbox = (id, opt) => {
    setFormData((p) => {
      const cur = p[id] || [];
      return { ...p, [id]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
    });
  };
  const updateRankingCell = (id, ri, col, v) => {
    setFormData((p) => {
      const rows = Array.isArray(p[id]) ? [...p[id]] : [];
      while (rows.length <= ri) rows.push({});
      rows[ri] = { ...rows[ri], [col]: v };
      return { ...p, [id]: rows };
    });
  };
  const handleFileUpload = async (id, file) => {
    if (!file) return;
    try {
      const { data } = await uploadPublicIntakeFile(token, file);
      updateField(id, { file_id: data.file_id, filename: file.name, size: file.size });
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error("Upload failed — try again");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitPublicIntakeForm(token, {
        submitter_name: meta?.recipient_name,
        data: formData,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try refreshing the page.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="h-12 w-12 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <XIcon className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold">Link Unavailable</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground">Please contact the person who sent you this link for a new one.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <Card className="max-w-md w-full" data-testid="public-intake-success">
          <CardContent className="pt-8 text-center space-y-3">
            <div className="h-14 w-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-green-600" />
            </div>
            <h1 className="text-2xl font-semibold">Thank you!</h1>
            <p className="text-sm text-muted-foreground">
              Your responses have been received. {meta.sender_label} will follow up with next steps shortly.
            </p>
            <p className="text-xs text-muted-foreground">You can close this tab.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="public-intake-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Brand-neutral header — shows the agency's logo + label, NEVER the portal's brand */}
        <div className="text-center space-y-3">
          {agencyLogoSrc && (
            <img
              src={agencyLogoSrc}
              alt={meta.sender_label || "Agency logo"}
              className="mx-auto max-h-16 w-auto object-contain"
              data-testid="public-intake-agency-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <p className="text-sm text-muted-foreground uppercase tracking-wide">{meta.sender_label}</p>
          <h1 className="font-outfit text-3xl font-bold tracking-tight mt-1">{schema?.name || meta.form_name}</h1>
          {schema?.description && (
            <p className="text-muted-foreground mt-2">{schema.description}</p>
          )}
        </div>

        {schema?.intro && (
          <div className="rounded-md border-l-4 border-primary bg-primary/5 px-4 py-3 text-sm leading-relaxed">
            {schema.intro}
          </div>
        )}

        {!schema && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              This form type can't be rendered. Please contact the person who sent the link.
            </CardContent>
          </Card>
        )}

        {schema && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {schema.sections.map((section, si) => (
              <Card key={si} className="border">
                <CardHeader>
                  <CardTitle className="text-lg">{section.title}</CardTitle>
                  {section.description && <CardDescription>{section.description}</CardDescription>}
                </CardHeader>
                <CardContent className="space-y-4">
                  {section.fields
                    .filter((field) => !field.condition || formData[field.condition.field] === field.condition.value)
                    .map((field) => (
                      <div key={field.id} className="space-y-2">
                        <Label htmlFor={field.id}>
                          {field.label} {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        {field.helper && <p className="text-xs text-muted-foreground -mt-1">{field.helper}</p>}

                        {field.type === "text" && (
                          <Input
                            id={field.id}
                            value={formData[field.id] || ""}
                            onChange={(e) => updateField(field.id, e.target.value)}
                            placeholder={field.placeholder || ""}
                            required={field.required}
                            data-testid={`public-field-${field.id}`}
                          />
                        )}

                        {field.type === "textarea" && (
                          <Textarea
                            id={field.id}
                            value={formData[field.id] || ""}
                            onChange={(e) => updateField(field.id, e.target.value)}
                            placeholder={field.placeholder || ""}
                            rows={3}
                            required={field.required}
                            data-testid={`public-field-${field.id}`}
                          />
                        )}

                        {field.type === "select" && (
                          <Select value={formData[field.id] || ""} onValueChange={(v) => updateField(field.id, v)}>
                            <SelectTrigger data-testid={`public-field-${field.id}`}>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {field.type === "checkboxes" && (
                          <div className="space-y-2">
                            {field.options.map((opt) => (
                              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                  checked={(formData[field.id] || []).includes(opt)}
                                  onCheckedChange={() => toggleCheckbox(field.id, opt)}
                                />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        {field.type === "ranking_table" && (
                          <div className="overflow-x-auto rounded-md border">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left font-medium px-3 py-2 w-16">Rank</th>
                                  {field.columns.map((c) => (
                                    <th key={c.key} className="text-left font-medium px-3 py-2">{c.label}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {Array.from({ length: field.rows || 5 }).map((_, ri) => {
                                  const row = (formData[field.id] || [])[ri] || {};
                                  return (
                                    <tr key={ri} className="border-t">
                                      <td className="px-3 py-2 text-muted-foreground font-medium">{ri + 1}</td>
                                      {field.columns.map((c) => (
                                        <td key={c.key} className="px-2 py-1.5">
                                          {c.type === "textarea" ? (
                                            <Textarea
                                              value={row[c.key] || ""}
                                              onChange={(e) => updateRankingCell(field.id, ri, c.key, e.target.value)}
                                              placeholder={c.placeholder || ""}
                                              rows={1}
                                              className="resize-y min-h-[36px]"
                                            />
                                          ) : (
                                            <Input
                                              value={row[c.key] || ""}
                                              onChange={(e) => updateRankingCell(field.id, ri, c.key, e.target.value)}
                                              placeholder={c.placeholder || ""}
                                            />
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {field.type === "file" && (
                          <div>
                            {formData[field.id]?.file_id ? (
                              <div className="flex items-center justify-between gap-3 rounded-md border bg-secondary/30 px-3 py-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="h-4 w-4 shrink-0 text-primary" />
                                  <span className="text-sm truncate" title={formData[field.id].filename}>
                                    {formData[field.id].filename}
                                  </span>
                                </div>
                                <Button type="button" variant="ghost" size="sm" onClick={() => updateField(field.id, null)}>
                                  <XIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <label
                                htmlFor={`upload-${field.id}`}
                                className="flex items-center justify-center gap-2 rounded-md border border-dashed bg-secondary/20 px-4 py-6 cursor-pointer hover:bg-secondary/40 transition"
                              >
                                <Upload className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Click to upload</span>
                                <input
                                  id={`upload-${field.id}`}
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => handleFileUpload(field.id, e.target.files?.[0])}
                                />
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-end">
              <Button type="submit" disabled={submitting} data-testid="public-intake-submit">
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" />Submit Form</>
                )}
              </Button>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground pt-4">
          This form is private and only shared with {meta.sender_label}.
        </p>
      </div>
    </div>
  );
}
